<template>
  <div class="space-y-4">
    <!-- Online Booking Alert - Mobile -->
    <UAlert
      v-if="cottagesData?.showOnlineBookingAlert"
      :title="t('views.cottage-list.results.alert.onlineBooking.title')"
      :description="
        t('views.cottage-list.results.alert.onlineBooking.description')
      "
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="vertical"
      :ui="alertOrangeIconMobile"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.onlineBooking.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: handleOnlineBookingFilter,
        },
      ]"
    />

    <!-- Online Booking Alert - Desktop -->
    <UAlert
      v-if="cottagesData?.showOnlineBookingAlert"
      :title="t('views.cottage-list.results.alert.onlineBooking.title')"
      :description="
        t('views.cottage-list.results.alert.onlineBooking.description')
      "
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="horizontal"
      :ui="alertOrangeIconDesktop"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.onlineBooking.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: handleOnlineBookingFilter,
        },
      ]"
    />

    <!-- Add Dates Alert - Mobile -->
    <UAlert
      v-if="cottagesData?.showAddDatesAlert"
      :title="t('views.cottage-list.results.alert.addDates.title')"
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="vertical"
      :ui="alertOrangeIconMobile"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.addDates.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: onToggleFilters,
        },
      ]"
    />

    <!-- Add Dates Alert - Desktop -->
    <UAlert
      v-if="cottagesData?.showAddDatesAlert"
      :title="t('views.cottage-list.results.alert.addDates.title')"
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="horizontal"
      :ui="alertOrangeIconDesktop"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.addDates.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: onToggleFilters,
        },
      ]"
    />

    <!-- Exact Capacity Alert - Mobile -->
    <UAlert
      v-if="cottagesData?.showExactPlacesAlert"
      :title="t('views.cottage-list.results.alert.exactCapacity.title')"
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="vertical"
      :ui="alertOrangeIconMobile"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.exactCapacity.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: handleExactCapacityFilter,
        },
      ]"
    />

    <!-- Exact Capacity Alert - Desktop -->
    <UAlert
      v-if="cottagesData?.showExactPlacesAlert"
      :title="t('views.cottage-list.results.alert.exactCapacity.title')"
      color="neutral"
      variant="subtle"
      icon="i-heroicons-light-bulb"
      orientation="horizontal"
      :ui="alertOrangeIconDesktop"
      :actions="[
        {
          label: t('views.cottage-list.results.alert.exactCapacity.action'),
          color: 'neutral',
          size: 'sm',
          variant: 'subtle',
          onClick: handleExactCapacityFilter,
        },
      ]"
    />

    <!-- Few Results Alert -->
    <UAlert
      v-if="
        cottagesData?.showFewResultsAlert &&
        cottagesData?.filterSuggestionsToRemove?.length > 0
      "
      :title="
        t('views.cottage-list.results.alert.fewResults.title', {
          n: cottagesData.totalCottages,
        })
      "
      color="neutral"
      variant="subtle"
      orientation="horizontal"
      :ui="{
        title: 'text-lg font-bold',
      }"
    >
      <template #description>
        <TText>{{
          t('views.cottage-list.results.alert.fewResults.description')
        }}</TText>
        <div class="mt-2 space-y-2">
          <div
            v-for="suggestion in cottagesData.filterSuggestionsToRemove"
            :key="suggestion.filterId"
            class="flex items-center"
          >
            <TText classes="mb-0 mr-3">{{ suggestion.name }}</TText>
            <UButton
              variant="subtle"
              color="neutral"
              @click="() => handleRemoveFilter(suggestion.filterId)"
            >
              {{ t('views.cottage-list.results.alert.fewResults.action') }} (+{{
                suggestion.cottagesNum
              }})
            </UButton>
          </div>
        </div>
      </template>
    </UAlert>
  </div>
</template>

<script setup lang="ts">
import type { schemaType } from '~~/modules/api/rurality/v3/search/cottages/schema'
import {
  alertOrangeIconMobile,
  alertOrangeIconDesktop,
} from '~~/modules/design-system/utils/nuxt-ui-config/components/alert'

interface Props {
  cottagesData?: schemaType
}

interface Emits {
  (e: 'toggle-filters'): void
  (e: 'online-booking-filter'): void
  (e: 'exact-capacity-filter'): void
  (e: 'remove-filter', filterId: number): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

function onToggleFilters() {
  emit('toggle-filters')
}

function handleOnlineBookingFilter() {
  emit('online-booking-filter')
}

function handleExactCapacityFilter() {
  emit('exact-capacity-filter')
}

function handleRemoveFilter(filterId: number) {
  emit('remove-filter', filterId)
}
</script>
